import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { KIND, SIZE } from "baseui/button";
import { BottomPanel } from "components/bottom-panel";
import { Button } from "components/button";
import { Cell } from "components/cell";
import { Content } from "components/content";
import { Filters } from "components/filters";
import { DataType, FormattedValue } from "components/formatted-value";
import { Grid } from "components/grid";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { PagingControls } from "components/pagination";
import { Table } from "components/table";
import SortingTableHeader, {
  SortDirection,
} from "components/table/sorting-table-header";
import { Tooltip } from "components/tooltip";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { usePaging } from "contexts/paging-context";
import { useProject } from "contexts/project-context";
import { BasicFilter, FiltersState } from "filters";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Row } from "react-table";
import { Eye, Plus } from "tabler-icons-react";
import { translateFiltersState } from "utils/filters";
import { PERMISSIONS } from "utils/permissions";
import { renderUserLabel } from "utils/render-user-label";

import { Article } from "../articles";
import { ARTICLES_FILTERS } from "../articles.filters";
import { ArticlesField } from "../articles.form";
import { ARTICLES_INDEX, ARTICLES_UPDATE } from "../articles.gql";

export default function ArticlesIndex(): React.ReactElement {
  const { pageSize, currentPage, setTotalCount } = usePaging();
  const [sortBy, setSortBy] = useState<ArticlesField | null>(
    ArticlesField.CreatedAt
  );
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(
    SortDirection.DESC
  );
  const { enqueueSnackbar } = useSnackbar();
  const [css] = useStyletron();
  const {
    isFetching,
    setIsFetching,
    setIsLoading,
    isPartialFetching,
    setIsPartialFetching,
  } = useLoading();
  const history = useHistory();
  const { checkPermission } = useAuth();
  const [filters, setFilters] = useState<FiltersState>();

  const { activeProject } = useProject();

  const handleSorting = (column: ArticlesField) => {
    setSortBy(column);
    setSortDirection(
      sortDirection === null
        ? SortDirection.DESC
        : sortDirection === SortDirection.ASC
        ? SortDirection.DESC
        : SortDirection.ASC
    );
  };

  const { refetch, data, loading, error } = useQuery(ARTICLES_INDEX, {
    variables: {
      pageSize,
      offset: (currentPage - 1) * pageSize,
      sorting: {
        field: sortBy,
        direction: sortDirection,
      },
      filter: {
        and: [
          {
            ...(activeProject && {
              projectId: {
                eq: activeProject?.id,
              },
            }),
          },
          {
            ...(filters && (translateFiltersState(filters) as BasicFilter[])),
          },
        ],
      },
    },
  });
  const articles: { totalCount: number; nodes: Article[] } = data?.articles;

  useEffect(() => {
    if (data?.articles) setTimeout(() => refetch(), 100);
    setIsFetching(true);
  }, []);

  useEffect(() => {
    refetch();
    setIsPartialFetching(true);
  }, [currentPage, pageSize]);

  useEffect(() => {
    refetch();
    setIsPartialFetching(true);
  }, [sortBy, sortDirection]);

  useEffect(() => {
    if (data?.articles) setIsFetching(false);
    if (articles?.totalCount >= 0) setTotalCount(articles?.totalCount);
  }, [data]);

  useEffect(() => {
    if (error?.graphQLErrors)
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [error]);

  const [updateArticle] = useMutation(ARTICLES_UPDATE);

  const onSubmit = async (article: Article): Promise<void> => {
    setIsLoading(true);

    try {
      await updateArticle({
        variables: {
          articleUpdateInput: {
            id: article.id,
            isVisible: !article.isVisible,
          },
        },
      });
      enqueueSnackbar({
        message: `Artykuł "${article.name}" jest ${
          article.isVisible ? "niewidoczny" : "widoczny"
        }`,
        variant: "success",
      });
      refetch();
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(ArticlesField.Name)}
            sortDirection={sortBy === ArticlesField.Name ? sortDirection : null}
          >
            Nazwa
          </SortingTableHeader>
        ),
        accessor: ArticlesField.Name,
        Cell: ({ row }: { row: Row<Article> }) => (
          <FormattedValue dataType={DataType.Articles} data={row.original.id}>
            {row.original.name}
          </FormattedValue>
        ),
        disableGlobalFilter: true,
      },
      {
        Header: "Projekt",
        accessor: ArticlesField.Project,
        Cell: ({ row }: { row: Row<Article> }) => (
          <FormattedValue
            dataType={DataType.Projects}
            data={row.original?.project?.id}
            $style={{ fontSize: "12px" }}
          >
            {row.original?.project?.name}
          </FormattedValue>
        ),
      },
      {
        Header: "Wersja językowa",
        accessor: ArticlesField.Language,
        Cell: ({ row }: { row: Row<Article> }) => (
          <FormattedValue
            dataType={DataType.Language}
            data={row.original?.project?.id}
            $style={{ fontSize: "12px" }}
          >
            {row.original?.language}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(ArticlesField.Slug)}
            sortDirection={sortBy === ArticlesField.Slug ? sortDirection : null}
          >
            Slug
          </SortingTableHeader>
        ),
        accessor: ArticlesField.Slug,
        Cell: ({ row }: { row: Row<Article> }) => (
          <FormattedValue dataType={DataType.Pre} $style={{ fontSize: "12px" }}>
            {row.original.slug}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <Block display="flex" justifyContent="flex-end">
            <SortingTableHeader
              onClick={() => handleSorting(ArticlesField.CreatedAt)}
              sortDirection={
                sortBy === ArticlesField.CreatedAt ? sortDirection : null
              }
            >
              Utworzenie
            </SortingTableHeader>
          </Block>
        ),
        accessor: ArticlesField.CreatedAt,
        Cell: ({ row }: { row: Row<Article> }) => (
          <Block display="flex" justifyContent="flex-end">
            <FormattedValue dataType={DataType.Date}>
              {row.original.createdAt}
            </FormattedValue>
          </Block>
        ),
      },
      {
        Header: "Utworzono przez",
        accessor: ArticlesField.CreatedBy,
        Cell: ({ row }: { row: Row<Article> }) => (
          <FormattedValue
            dataType={DataType.Users}
            data={row.original?.createdBy?.id}
          >
            {row.original?.createdBy
              ? renderUserLabel(row.original?.createdBy)
              : undefined}
          </FormattedValue>
        ),
      },
      {
        Header: "Autor",
        accessor: ArticlesField.Author,
        Cell: ({ row }: { row: Row<Article> }) =>
          row?.original?.author?.name ? (
            <FormattedValue>{row?.original?.author?.name}</FormattedValue>
          ) : row?.original?.author?.user ? (
            <FormattedValue
              dataType={DataType.Users}
              data={row?.original?.author?.user?.id}
            >
              {renderUserLabel(row?.original?.author?.user)}
            </FormattedValue>
          ) : (
            <FormattedValue />
          ),
      },
      {
        Header: (
          <Block display="flex" justifyContent="flex-end">
            <SortingTableHeader
              onClick={() => handleSorting(ArticlesField.UpdatedAt)}
              sortDirection={
                sortBy === ArticlesField.UpdatedAt ? sortDirection : null
              }
            >
              Ostatnia aktualizacja
            </SortingTableHeader>
          </Block>
        ),
        accessor: ArticlesField.UpdatedAt,
        Cell: ({ row }: { row: Row<Article> }) => (
          <Block display="flex" justifyContent="flex-end">
            <FormattedValue dataType={DataType.Date}>
              {row.original.updatedAt}
            </FormattedValue>
          </Block>
        ),
      },
      {
        Header: "Zaktualizowano przez",
        accessor: ArticlesField.UpdatedBy,
        Cell: ({ row }: { row: Row<Article> }) => (
          <FormattedValue
            dataType={DataType.Users}
            data={row.original?.updatedBy?.id}
          >
            {row.original?.updatedBy
              ? renderUserLabel(row.original?.updatedBy)
              : undefined}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(ArticlesField.IsVisible)}
            sortDirection={
              sortBy === ArticlesField.IsVisible ? sortDirection : null
            }
          >
            Widoczność
          </SortingTableHeader>
        ),
        accessor: ArticlesField.IsVisible,
        Cell: ({ row }: { row: Row<Article> }) => (
          <Tooltip
            content={`Ustaw jako ${
              row.original.isVisible ? "niewidoczny" : "widoczny"
            }`}
            placement="right"
          >
            <span
              onClick={() => onSubmit(row.original)}
              className={css({
                cursor: "pointer",
              })}
            >
              <FormattedValue dataType={DataType.VisibilityBoolean}>
                {row.original.isVisible}
              </FormattedValue>
            </span>
          </Tooltip>
        ),
      },
      {
        id: "actions",
        Cell: ({ row }: { row: Row<Article> }) => (
          <div
            className={css({
              display: "flex",
              justifyContent: "flex-end",
            })}
          >
            <Button
              kind={KIND.secondary}
              size={SIZE.mini}
              $style={{ marginLeft: "6px" }}
              onClick={() => history.push(`/articles/${row.original.id}`)}
              startEnhancer={<Eye size={14} />}
            />
          </div>
        ),
      },
    ],
    [sortBy, sortDirection]
  );

  if (!checkPermission(PERMISSIONS.article.read))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title="Artykuły"
        recordsNum={articles?.totalCount}
        labels={["Lista"]}
        buttons={[
          {
            label: "Dodaj artykuł",
            kind: KIND.primary,
            startEnhancer: <Plus size={18} />,
            permission: checkPermission(PERMISSIONS.article.create),
            onClick: () => history.push("/articles/create"),
          },
        ]}
      />
      <Filters
        filters={ARTICLES_FILTERS.filter((filter: BasicFilter) =>
          activeProject ? filter.id !== ArticlesField.Project : filter
        )}
        state={filters}
        setState={setFilters}
      />
      <Content filtersOffset>
        <Grid>
          <Cell span={12}>
            <Table<Article>
              columns={columns}
              data={articles?.nodes}
              isLoading={isFetching || isPartialFetching || loading}
              stickLastColumn
            />
          </Cell>
          <Cell span={12}>
            <BottomPanel>
              <PagingControls />
            </BottomPanel>
          </Cell>
        </Grid>
      </Content>
    </article>
  );
}
